const PROTOCOL = 'easyllama-rxd-v1';
const TIMEOUT_MS = 5000;
const MAX_CALLS = 32;
const METHODS = [
  'SetProgressMeasure',
  'SetScore',
  'SetReachedEnd',
  'SetPassed',
  'ConcedeControl',
  'RecordMultipleChoiceInteraction',
  'RecordTrueFalseInteraction',
  'RecordSequencingInteraction',
  'RecordFillInInteraction'
] as const;

export type RxdMethod = (typeof METHODS)[number];

export interface RxdTransport {
  readonly isAvailable: boolean;
  call(method: RxdMethod, args: readonly unknown[]): Promise<unknown>;
  destroy(): void;
}

class BridgeError extends Error {
  constructor(readonly code: string) {
    super(code);
  }
}

interface Pending {
  timer: ReturnType<typeof setTimeout>;
  resolve(value: unknown): void;
  reject(error: Error): void;
}

function bridgeHost(): { parent: Window; origin: string } | undefined {
  try {
    if (window.parent === window) return;

    // The wrapper stays on Rails' l.* host when its child redirects to the SPA.
    // Derive that one trusted origin from deployment config, never URL input.
    const backend = new URL(process.env.REACT_APP_BACKEND_API_BASE_URL || '');
    if (!/^https?:$/.test(backend.protocol)) return;
    const preview = /^([a-z0-9-]+)\.api\.rails\.llamagenie\.io$/.exec(
      backend.hostname
    );
    if (preview && backend.protocol === 'https:' && !backend.port) {
      backend.hostname = `${preview[1]}.l.rails.llamagenie.io`;
    } else if (backend.hostname.startsWith('api.')) {
      backend.hostname = `l.${backend.hostname.slice(4)}`;
    } else return;
    try {
      // A readable parent on another origin is a known non-RXD host (for
      // example a free package on the app origin). It needs no RPC probe.
      if (window.parent.location.origin !== backend.origin) return;
    } catch {
      // Cross-origin parents are authenticated by the correlated hello reply.
    }
    return {
      parent: window.parent,
      origin: backend.origin
    };
  } catch {
    return;
  }
}

/** A narrowly scoped RPC client for the existing RXD methods, not a SCORM API. */
export function createRxdTransport(): RxdTransport | undefined {
  // Auth cleanup and iframe reloads erase launch markers and change referrers.
  // Probe only the configured parent origin; a correlated reply from that exact
  // window is required, and the relay must have registered this learner origin.
  const host = bridgeHost();
  if (!host) return;
  let nonce: string;
  try {
    nonce = window.crypto.randomUUID();
  } catch {
    return;
  }

  const pending = new Map<string, Pending>();
  let sequence = 0;
  let available = false;
  let closed = false;
  let calls = 0;
  let cancelRetry: (() => void) | undefined;

  const receive = (event: MessageEvent) => {
    if (event.source !== host.parent || event.origin !== host.origin) return;
    const data = event.data;
    if (
      !data ||
      data.protocol !== PROTOCOL ||
      data.action !== 'response' ||
      typeof data.id !== 'string' ||
      typeof data.ok !== 'boolean'
    )
      return;
    const entry = pending.get(data.id);
    if (!entry) return;
    pending.delete(data.id);
    clearTimeout(entry.timer);
    if (data.ok) entry.resolve(data.result);
    else
      entry.reject(
        new BridgeError(
          typeof data.error === 'string' ? data.error : 'invalid-response'
        )
      );
  };
  window.addEventListener('message', receive);

  const request = (payload: object, timeout = TIMEOUT_MS): Promise<unknown> =>
    new Promise((resolve, reject) => {
      const id = `${nonce}:${++sequence}`;
      const timer = setTimeout(() => {
        pending.delete(id);
        reject(new BridgeError('timeout'));
      }, timeout);
      pending.set(id, { timer, resolve, reject });
      try {
        host.parent.postMessage(
          { protocol: PROTOCOL, id, ...payload },
          host.origin
        );
      } catch (error) {
        clearTimeout(timer);
        pending.delete(id);
        reject(error);
      }
    });

  const establish = async () => {
    const deadline = Date.now() + TIMEOUT_MS;
    while (!closed && Date.now() < deadline) {
      try {
        const result = await request(
          { action: 'hello' },
          deadline - Date.now()
        );
        available =
          !closed &&
          !!result &&
          (result as { available?: unknown }).available === true;
        return available;
      } catch (error) {
        if (
          closed ||
          Date.now() >= deadline ||
          !(error instanceof BridgeError) ||
          error.code !== 'not-ready'
        )
          return false;
        await new Promise<void>(resolve => {
          const timer = setTimeout(
            () => {
              cancelRetry = undefined;
              resolve();
            },
            Math.min(100, Math.max(0, deadline - Date.now()))
          );
          cancelRetry = () => {
            clearTimeout(timer);
            cancelRetry = undefined;
            resolve();
          };
        });
      }
    }
    return false;
  };
  const ready = establish();

  return {
    get isAvailable() {
      return available;
    },
    async call(method, args) {
      if (closed) throw new BridgeError('closed');
      if (!METHODS.includes(method) || !Array.isArray(args))
        throw new BridgeError('invalid-method');
      if (calls >= MAX_CALLS) throw new BridgeError('too-many-pending-calls');
      calls++;
      try {
        if (!(await ready) || closed) throw new BridgeError('not-ready');
        return await request({ action: 'call', method, args });
      } finally {
        calls--;
      }
    },
    destroy() {
      closed = true;
      available = false;
      window.removeEventListener('message', receive);
      cancelRetry?.();
      pending.forEach(entry => {
        clearTimeout(entry.timer);
        entry.reject(new BridgeError('closed'));
      });
      pending.clear();
    }
  };
}
